import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Skeleton, SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraText } from '@mono/ui-components';
import { useEffect, useState } from 'react';
import { businessPhaseLabel, lifecycleLabel } from '../CaptureLifecyclePanel';
import { useCapturePlan, useCaptureWorkspace, usePursuitOpening } from '../hooks';
import { isPermissionDenied, useCaptureTeam } from '../teamHooks';
import { buildPursuitOverview } from './pursuitProjection';
import { useOverviewSources } from './useOverviewSources';
function IssueList({ items, onOpen, empty }) {
    if (items.length === 0) return /*#__PURE__*/ _jsx(SpecraText, {
        tone: "muted",
        children: empty
    });
    return /*#__PURE__*/ _jsx("ul", {
        className: "space-y-2",
        children: items.map((item)=>/*#__PURE__*/ _jsxs("li", {
                className: "flex flex-wrap items-center justify-between gap-2 border-b border-border py-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                className: "block",
                                children: item.title
                            }),
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                className: "block",
                                size: "meta",
                                tone: "muted",
                                children: [
                                    item.condition,
                                    " · Date: ",
                                    item.date
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        onClick: ()=>onOpen(item.destination),
                        "aria-label": `Open ${item.title}`,
                        children: "Open"
                    })
                ]
            }, item.key))
    });
}
export function PursuitOverview({ captureProjectId, onOpen, onProfile, onTeam }) {
    const workspace = useCaptureWorkspace(captureProjectId);
    if (workspace.isLoading) {
        return /*#__PURE__*/ _jsxs("section", {
            "aria-label": "Pursuit overview",
            "aria-busy": "true",
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Loading pursuit overview…"
                }),
                /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-8 w-1/3"
                }),
                /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-40 w-full"
                })
            ]
        });
    }
    if (workspace.isError || !workspace.data?.summary) {
        return /*#__PURE__*/ _jsxs("section", {
            "aria-label": "Pursuit overview",
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    children: isPermissionDenied(workspace.error) ? 'This pursuit is unavailable or you do not have access.' : 'Pursuit overview unavailable.'
                }),
                !isPermissionDenied(workspace.error) && /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>workspace.refetch(),
                    children: "Retry overview"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(AuthorizedOverview, {
        captureProjectId,
        onOpen,
        onProfile,
        onTeam,
        workspace
    });
}
function AuthorizedOverview({ captureProjectId, onOpen, onProfile, onTeam, workspace }) {
    const plan = useCapturePlan(captureProjectId);
    const team = useCaptureTeam(captureProjectId);
    const opening = usePursuitOpening(captureProjectId);
    const sources = useOverviewSources(captureProjectId, workspace.data?.summary?.zouProjectId ?? '');
    const [now, setNow] = useState(()=>new Date());
    useEffect(()=>{
        const timer = setInterval(()=>setNow(new Date()), 60000);
        return ()=>clearInterval(timer);
    }, []);
    const data = workspace.data;
    if (!data?.summary) return null;
    const summary = data.summary;
    if (!summary) return null;
    const model = buildPursuitOverview(data, plan.isSuccess ? plan.data.areas : [], now, sources);
    const profile = data.profile;
    const members = team.isSuccess && !team.isError ? team.data : [];
    const owner = members.find((member)=>member.isOwner);
    const refresh = ()=>Promise.all([
            workspace.refetch(),
            plan.refetch(),
            team.refetch(),
            sources.refetch(),
            opening.refetch()
        ]);
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": "Pursuit overview",
        className: "space-y-4",
        "aria-busy": workspace.isFetching || plan.isFetching || team.isFetching || opening.isFetching,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCard, {
                children: [
                    /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                        className: "flex flex-row items-center justify-between gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraCardTitle, {
                                children: "Pursuit at a glance"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                onClick: refresh,
                                children: "Refresh overview"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                children: summary.opportunityHypothesis || 'Pursued scope / hypothesis: missing.'
                            }),
                            /*#__PURE__*/ _jsxs("dl", {
                                className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Business phase"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: businessPhaseLabel(summary.businessPhase)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Capture emphasis"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: profile?.stageLabel || 'Unknown'
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Pursuit lifecycle"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: lifecycleLabel(summary.lifecycle)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Qualification"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: /*#__PURE__*/ _jsx(QualificationContext, {
                                                    opening: opening
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Customer / agency"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: profile?.customerAgencyNames || 'Missing'
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Accountable owner"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: team.isError ? 'Unavailable' : team.isLoading ? 'Loading…' : owner?.displayName || 'Not assigned'
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Next open task due"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: model.nextTaskDate
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Next open review"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: model.nextReviewDate
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Next Capture Area target"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: model.nextAreaTargetDate
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Period of performance"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: profile?.periodOfPerformance || 'Unknown'
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Program value estimate"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: profile?.totalProgramValue || 'Unknown'
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Win probability estimate"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: profile?.pwinPercent ? `${profile.pwinPercent}%` : 'Unknown'
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("dt", {
                                                children: "Risks / issues"
                                            }),
                                            /*#__PURE__*/ _jsx("dd", {
                                                children: profile?.risks || 'None recorded'
                                            })
                                        ]
                                    })
                                ]
                            }),
                            members.length > 0 && /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                children: members.map((member)=>`${member.displayName} (${member.isOwner ? 'Accountable owner' : member.role || 'Contributor'})`).join(' · ')
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "secondary",
                                        onClick: onProfile,
                                        children: "Open full profile"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "secondary",
                                        onClick: onTeam,
                                        children: "Open pursuit team"
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraButton, {
                                        size: "sm",
                                        variant: "secondary",
                                        onClick: ()=>onOpen({
                                                kind: 'task',
                                                id: ''
                                            }),
                                        children: [
                                            model.openTasks,
                                            " open task",
                                            model.openTasks === 1 ? '' : 's',
                                            " · ",
                                            model.overdueTasks,
                                            ' ',
                                            "overdue"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraButton, {
                                        size: "sm",
                                        variant: "secondary",
                                        onClick: ()=>onOpen({
                                                kind: 'decision',
                                                id: ''
                                            }),
                                        children: [
                                            model.legacyPendingDecisions,
                                            " pending decision",
                                            model.legacyPendingDecisions === 1 ? '' : 's'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        size: "meta",
                                        children: [
                                            model.deferredDecisions,
                                            " deferred decision",
                                            model.deferredDecisions === 1 ? '' : 's',
                                            ' ',
                                            "across current scopes"
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCard, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardHeader, {
                        children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Permitted connected work"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                children: sources.isLoading ? 'Loading linked work…' : `${model.links.length} linked workspace${model.links.length === 1 ? '' : 's'} · ${model.interactions.length} interaction${model.interactions.length === 1 ? '' : 's'}`
                            }),
                            model.links.map((link)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "ghost",
                                    size: "sm",
                                    onClick: ()=>onOpen({
                                            kind: 'source',
                                            id: link.key,
                                            href: link.href
                                        }),
                                    children: link.title
                                }, link.key)),
                            /*#__PURE__*/ _jsx(IssueList, {
                                items: model.interactions,
                                onOpen: onOpen,
                                empty: "No permitted interactions available."
                            }),
                            sources.failed && /*#__PURE__*/ _jsxs(SpecraText, {
                                tone: "muted",
                                children: [
                                    sources.failed,
                                    " unavailable; totals may be incomplete. Refresh to retry."
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCard, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardHeader, {
                        children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Needs attention"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        children: [
                            /*#__PURE__*/ _jsx(IssueList, {
                                items: model.attention,
                                onOpen: onOpen,
                                empty: "No actionable conditions in the available records."
                            }),
                            plan.isError && /*#__PURE__*/ _jsx(SpecraText, {
                                tone: "muted",
                                children: "Capture Area attention unavailable. Retry the overview."
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCard, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardHeader, {
                        children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Capture Area health"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraCardContent, {
                        className: "space-y-2",
                        children: plan.isLoading ? /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Loading Capture Areas…"
                        }) : plan.isError ? /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Capture Areas unavailable; health unknown. Refresh to retry."
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    tone: "muted",
                                    children: model.areaSummary
                                }),
                                (plan.data?.areas ?? []).map((area)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-wrap items-center justify-between gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "ghost",
                                                size: "sm",
                                                onClick: ()=>onOpen({
                                                        kind: 'area',
                                                        id: area.id
                                                    }),
                                                children: area.name || 'Unnamed area'
                                            }),
                                            /*#__PURE__*/ _jsxs(SpecraBadge, {
                                                variant: area.health === 'at_risk' ? 'warning' : 'neutral',
                                                children: [
                                                    area.health ? area.health.replace(/_/g, ' ') : 'Unknown',
                                                    " · Target:",
                                                    ' ',
                                                    area.targetDate ? area.targetDate.toDate().toLocaleDateString() : 'Unknown',
                                                    " ·",
                                                    ' ',
                                                    area.summary || 'Summary missing'
                                                ]
                                            })
                                        ]
                                    }, area.id))
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCard, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardHeader, {
                        children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Recent meaningful activity"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraCardContent, {
                        children: /*#__PURE__*/ _jsx(IssueList, {
                            items: model.activity,
                            onOpen: onOpen,
                            empty: "No dated interactions or recorded decisions available."
                        })
                    })
                ]
            })
        ]
    });
}
function QualificationContext({ opening }) {
    // Query caches can retain data after a revoked permission or failed refresh.
    if (opening.isError) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            children: isPermissionDenied(opening.error) ? 'Qualification context is unavailable or you do not have access.' : 'Qualification context unavailable. Refresh to retry.'
        });
    }
    if (opening.isLoading) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "Loading Qualification context…"
    });
    if (!opening.isSuccess) return null;
    const snapshot = opening.data?.qualificationHandoff;
    if (!snapshot) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "No Qualification context was handed off."
    });
    // Read only the explicit handoff snapshot, never the live evaluation answers.
    let items;
    try {
        const parsed = JSON.parse(snapshot.contextJson);
        if (!Array.isArray(parsed) || parsed.some((item)=>!item || typeof item !== 'object' || !(item.kind === 'rationale' && item.key === 'rationale' || item.kind === 'answer' && typeof item.criterion_id === 'string' && item.key === `answer:${item.criterion_id}`) || !('value' in item))) throw new Error('Invalid handoff');
        items = parsed;
    } catch  {
        return /*#__PURE__*/ _jsx(SpecraText, {
            children: "Qualification context unavailable. Refresh to retry."
        });
    }
    const display = (value)=>typeof value === 'string' ? value : JSON.stringify(value);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsxs(SpecraText, {
                children: [
                    "Disposition at handoff: ",
                    snapshot.disposition || 'Unknown'
                ]
            }),
            items.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                children: "No answers or rationale were selected for this handoff."
            }) : /*#__PURE__*/ _jsx("ul", {
                children: items.map((item)=>/*#__PURE__*/ _jsxs("li", {
                        children: [
                            item.kind === 'rationale' ? 'Rationale' : item.criterion_id,
                            ": ",
                            display(item.value),
                            item.basis != null && /*#__PURE__*/ _jsxs(SpecraText, {
                                size: "meta",
                                children: [
                                    "Answer basis: ",
                                    display(item.basis)
                                ]
                            })
                        ]
                    }, item.key))
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                className: "block",
                size: "meta",
                children: [
                    "Evaluation: ",
                    snapshot.evaluationId,
                    " · Source event: ",
                    snapshot.sourceEventId,
                    " · Handoff event: ",
                    snapshot.handoffEventId
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                className: "block",
                size: "meta",
                children: [
                    "Template version: ",
                    snapshot.templateVersion,
                    " (",
                    snapshot.templateVersionId,
                    ")"
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                className: "block",
                size: "meta",
                children: [
                    "Reviewer: ",
                    snapshot.reviewer || 'Unknown',
                    " · Handoff actor: ",
                    snapshot.actor || 'Unknown',
                    " · Handed off: ",
                    snapshot.createdAt?.toDate().toISOString() || 'Unknown'
                ]
            }),
            opening.data?.opportunityId && /*#__PURE__*/ _jsx("a", {
                href: `/opportunities?qualification=${encodeURIComponent(opening.data.opportunityId)}`,
                children: "Open originating Qualification"
            })
        ]
    });
}
